import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import AccountSelector from '../../components/AccountSelector';
import { formatMoney } from '../../utils/money';
import ModalRoot from '../../components/ModalRoot';

const STATUS_COLORS = { pending: '#f59e0b', approved: '#10b981', denied: '#dc2626', paid: '#6d28d9', recorded: '#64748b', voided: '#475569' };
const STATUS_LABELS = { pending: 'Pending', approved: 'Approved', denied: 'Denied', paid: 'Paid', recorded: 'Recorded', voided: 'Voided' };
const TYPE_LABELS = { food: 'Food', gift: 'Gift', other: 'Other' };
const CATEGORY_LABELS = { sales: 'Sales', drivers: 'Drivers', other: 'Other' };
const CATEGORY_COLORS = { sales: '#6d28d9', drivers: '#0891b2', other: '#64748b' };

// Central-time date helpers — matches the locked history-page pattern
// (RequestBucketPage.js). Timeframe presets all use Chicago time so
// "Today" means today in Apollo's actual workday, not UTC.
function dateToCT(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date);
  const y = parts.find(p => p.type === 'year').value;
  const m = parts.find(p => p.type === 'month').value;
  const d = parts.find(p => p.type === 'day').value;
  return `${y}-${m}-${d}`;
}
const getTodayCT = () => dateToCT(new Date());
const getLast7DaysCT = () => dateToCT(new Date(Date.now() - 7 * 86400000));
function getThisMonthStartCT() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago', year: 'numeric', month: '2-digit',
  }).formatToParts(new Date());
  const y = parts.find(p => p.type === 'year').value;
  const m = parts.find(p => p.type === 'month').value;
  return `${y}-${m}-01`;
}

export default function ExpenseHistoryPage() {
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const canManage = can('manage_tasks');
  const isSuperAdmin = user?.permission_tier === 'super_admin';

  const [expenses, setExpenses] = useState([]);
  const [total, setTotal] = useState(0);
  const [mtdTotal, setMtdTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);

  // Filters — follow locked history-page pattern (feedback_history_page_pattern.md).
  // Default = This Month preset; user can switch to Today / Last 7 Days / Custom.
  const [activePreset, setActivePreset] = useState('month');
  const [startDate, setStartDate] = useState(getThisMonthStartCT());
  const [endDate, setEndDate] = useState(getTodayCT());
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [reimbFilter, setReimbFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');

  function applyPreset(preset) {
    setActivePreset(preset);
    if (preset === 'today') {
      const t = getTodayCT();
      setStartDate(t); setEndDate(t);
    } else if (preset === 'last7') {
      setStartDate(getLast7DaysCT()); setEndDate(getTodayCT());
    } else if (preset === 'month') {
      setStartDate(getThisMonthStartCT()); setEndDate(getTodayCT());
    }
    // 'custom' = leave dates as the user enters them
  }

  function clearFilters() {
    applyPreset('month');
    setStatusFilter(''); setTypeFilter(''); setPropertyFilter('');
    setEmployeeFilter(''); setReimbFilter(''); setCategoryFilter(''); setSearch('');
  }

  // Sorting — click cycle: null → asc → desc → null
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('');

  // Review modal

  // Read-only detail modal (company expenses only — reimbursables go to the
  // workflow item page where the actions live).
  const [viewExpense, setViewExpense] = useState(null);

  // Link-to-account modal
  const [linkExpense, setLinkExpense] = useState(null);
  const [linkAccount, setLinkAccount] = useState(null);
  const [linkUseOther, setLinkUseOther] = useState(false);
  const [linking, setLinking] = useState(false);

  // ── Batch payment selection (admin-only) ─────────────────────
  // Multi-select unpaid approved reimbursements for the SAME recipient,
  // then process them as one check. Mirrors QuickBooks "Receive Payment"
  // applying one payment to multiple invoices.
  const [batchSelectedIds, setBatchSelectedIds] = useState([]); // expense ids
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [batchCheckNumber, setBatchCheckNumber] = useState('');
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [batchError, setBatchError] = useState('');

  // Recipient = expense_for || submitted_by — same logic as the server
  const recipientIdOf = (e) => e.expense_for || e.submitted_by;
  const recipientNameOf = (e) => e.employee_name || e.submitter_name || `User #${recipientIdOf(e)}`;

  // Anchor recipient = whoever was selected first; locks the rest of the rows
  const batchAnchorId = batchSelectedIds.length > 0
    ? recipientIdOf(expenses.find(x => x.id === batchSelectedIds[0]) || {})
    : null;
  const batchAnchorName = batchSelectedIds.length > 0
    ? recipientNameOf(expenses.find(x => x.id === batchSelectedIds[0]) || {})
    : null;

  const isBatchableRow = (exp) => (
    canManage && exp.is_reimbursement && exp.status === 'approved' && !exp.paid_at
  );

  const isBatchSelectableForRow = (exp) => {
    if (!isBatchableRow(exp)) return false;
    if (batchAnchorId == null) return true;
    return recipientIdOf(exp) === batchAnchorId;
  };

  const toggleBatch = (exp) => {
    setBatchSelectedIds(prev =>
      prev.includes(exp.id) ? prev.filter(x => x !== exp.id) : [...prev, exp.id]
    );
  };

  const clearBatch = () => {
    setBatchSelectedIds([]);
    setBatchModalOpen(false);
    setBatchCheckNumber('');
    setBatchError('');
  };

  const batchTotal = batchSelectedIds
    .map(id => expenses.find(e => e.id === id))
    .filter(Boolean)
    .reduce((s, e) => s + Number(e.amount || 0), 0);

  const submitBatch = async () => {
    setBatchSubmitting(true);
    setBatchError('');
    try {
      await api.batchMarkExpensesPaid({
        expense_ids: batchSelectedIds,
        check_number: batchCheckNumber.trim() || null,
      });
      clearBatch();
      await loadExpenses();
    } catch (err) {
      setBatchError(err.message);
    } finally {
      setBatchSubmitting(false);
    }
  };

  useEffect(() => {
    api.getProperties().then(p => setProperties(Array.isArray(p) ? p : [])).catch(() => {});
    if (canManage) {
      api.getUsers().then(u => setUsers(Array.isArray(u) ? u.filter(x => x.is_active) : [])).catch(() => {});
    }
  }, [canManage]);

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const filters = { limit: 200 };
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      if (statusFilter) filters.status = statusFilter;
      if (typeFilter) filters.reimbursement_type = typeFilter;
      if (propertyFilter) filters.property_id = propertyFilter;
      if (employeeFilter) filters.expense_for = employeeFilter;
      if (reimbFilter !== '') filters.is_reimbursement = reimbFilter;
      if (categoryFilter) filters.category = categoryFilter;
      if (search) filters.search = search;
      if (sortBy) { filters.sort_by = sortBy; filters.sort_dir = sortDir; }

      // Pass the SAME filter set to /stats so the KPI cards always
      // reflect the active filtered view (locked history-page rule).
      // limit/offset/sort_by/sort_dir aren't relevant to aggregations.
      const statsFilters = { ...filters };
      delete statsFilters.limit; delete statsFilters.offset;
      delete statsFilters.sort_by; delete statsFilters.sort_dir;
      const [data, statsData] = await Promise.all([
        api.getExpenses(filters),
        api.getExpenseStats(statsFilters),
      ]);
      setExpenses(data.expenses || []);
      setTotal(data.total || 0);
      setMtdTotal(statsData?.filtered_amount ?? data.mtd_total ?? 0);
      setStats(statsData || {});
    } catch { /* ignore */ }
    setLoading(false);
  }, [startDate, endDate, statusFilter, typeFilter, propertyFilter, employeeFilter, reimbFilter, categoryFilter, search, sortBy, sortDir]);

  useEffect(() => { loadExpenses(); }, [loadExpenses]);

  function handleSort(column) {
    if (sortBy !== column) {
      setSortBy(column);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortBy('');
      setSortDir('');
    }
  }

  function sortArrow(column) {
    if (sortBy !== column) return '';
    return sortDir === 'asc' ? ' \u25B2' : ' \u25BC';
  }

  async function handleLink() {
    if (!linkAccount) return;
    setLinking(true);
    try {
      await api.linkExpenseAccounts(linkExpense.id, [linkAccount.id]);
      setLinkExpense(null);
      setLinkAccount(null);
      loadExpenses();
    } catch { /* ignore */ }
    setLinking(false);
  }

  async function handleExport() {
    try {
      const filters = {};
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      if (statusFilter) filters.status = statusFilter;
      if (typeFilter) filters.reimbursement_type = typeFilter;
      if (propertyFilter) filters.property_id = propertyFilter;
      if (employeeFilter) filters.expense_for = employeeFilter;
      if (reimbFilter !== '') filters.is_reimbursement = reimbFilter;
      if (categoryFilter) filters.category = categoryFilter;
      if (search) filters.search = search;

      const blob = await api.exportExpenses(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `expenses-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  }

  function formatDate(d) {
    if (!d) return '';
    return new Date(d + (d.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function getAccountName(exp) {
    if (exp.linked_accounts && exp.linked_accounts.length > 0) {
      return exp.linked_accounts.map(a => a.name).join(', ');
    }
    return exp.property_name || exp.account_name_other || '—';
  }

  function getEmployeeName(exp) {
    return exp.employee_name || exp.submitter_name || '—';
  }

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>Expenses</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-gold" onClick={() => navigate('/sales/expenses/new')} style={{ fontWeight: 700 }}>
            + Submit new Expense
          </button>
          {canManage && (
            <button className="btn btn-gold" onClick={handleExport} title="Export to Excel" style={{ fontWeight: 700 }}>
              Export Excel
            </button>
          )}
        </div>
      </div>

      {/* KPI cards — locked history-page pattern: Total / Submitted / Completed / Amount.
          Reflect the FILTERED set (total / pending / approved counts from server stats).
          'Amount' is the sum of the filtered period — money-relevant for expenses. */}
      <div className="kpi-grid">
        {[
          { label: 'Total',     value: total,                                                     color: '#0f172a' },
          { label: 'Submitted', value: stats.pending_count  || 0,                                 color: '#3b82f6' },
          { label: 'Completed', value: (stats.approved_count || 0) + (stats.paid_count || 0),     color: '#10b981' },
          { label: 'Amount',    value: formatMoney(mtdTotal),                                     color: '#8b5cf6' },
        ].map(s => (
          <div key={s.label} className="kpi-card" style={{ '--kpi-accent': s.color }}>
            <div className="kpi-value">{s.value}</div>
            <div className="kpi-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter card — locked history-page pattern: 4 timeframe presets, then
          standard filter row, then search field + Clear. */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        {/* 1. Timeframe presets */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          {[
            { key: 'today', label: 'Today' },
            { key: 'last7', label: 'Last 7 Days' },
            { key: 'month', label: 'This Month' },
            { key: 'custom', label: 'Custom' },
          ].map(p => {
            const selected = activePreset === p.key;
            return (
              <button key={p.key} type="button" onClick={() => applyPreset(p.key)}
                style={{
                  padding: '8px 16px', fontSize: 13, fontWeight: 700, borderRadius: 8,
                  border: `2px solid ${selected ? '#3b82f6' : '#e2e8f0'}`,
                  background: selected ? '#3b82f6' : '#fff',
                  color: selected ? '#fff' : '#0f172a',
                  cursor: 'pointer', transition: 'all 0.15s ease',
                }}
              >{p.label}</button>
            );
          })}
          {activePreset === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 4 }}>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }} />
              <span style={{ color: '#94a3b8', fontSize: 13 }}>to</span>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }} />
            </div>
          )}
        </div>

        {/* 2. Standard filter row: Property + Status + Submitted By + Kind + Category + Search + Clear */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {canManage && (
            <select value={propertyFilter} onChange={e => setPropertyFilter(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, minWidth: 160 }}>
              <option value="">Property name…</option>
              {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}>
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved (Unpaid)</option>
            <option value="paid">Paid</option>
            <option value="denied">Denied</option>
          </select>
          {canManage && (
            <select value={employeeFilter} onChange={e => setEmployeeFilter(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, minWidth: 160 }}>
              <option value="">Submitted By</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}
            </select>
          )}
          {canManage && (
            <select value={reimbFilter} onChange={e => setReimbFilter(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}>
              <option value="">All Kinds</option>
              <option value="1">Reimbursement</option>
              <option value="0">Company Expense</option>
            </select>
          )}
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}>
            <option value="">All Categories</option>
            <option value="sales">Sales</option>
            <option value="drivers">Drivers</option>
            <option value="other">Other</option>
          </select>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}>
            <option value="">All Types</option>
            <option value="food">Food</option>
            <option value="gift">Gift</option>
            <option value="other">Other</option>
          </select>
          <input type="text" placeholder="Search reason / vehicle / notes…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, minWidth: 200, flex: 1 }} />
          {(activePreset !== 'month' || statusFilter || typeFilter || propertyFilter || employeeFilter || reimbFilter || categoryFilter || search) && (
            <button type="button" onClick={clearFilters}
              style={{ fontSize: 12, color: '#6d28d9', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
              Clear filters
            </button>
          )}
          <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>{total} result{total !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : expenses.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>&#128203;</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>No expenses found</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            {startDate || endDate ? 'Try adjusting your date range or filters' : 'Showing Month-to-Date by default'}
          </div>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                {canManage && (
                  <th style={{ width: 36, padding: '0 4px' }} aria-label="Select for batch payment"></th>
                )}
                <th onClick={() => handleSort('expense_date')} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                  Date{sortArrow('expense_date')}
                </th>
                <th onClick={() => handleSort('employee')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Employee{sortArrow('employee')}
                </th>
                <th onClick={() => handleSort('account')} style={{ cursor: 'pointer', userSelect: 'none' }} className="hide-mobile">
                  Account{sortArrow('account')}
                </th>
                <th onClick={() => handleSort('reimbursement_type')} style={{ cursor: 'pointer', userSelect: 'none' }} className="hide-mobile">
                  Type{sortArrow('reimbursement_type')}
                </th>
                <th onClick={() => handleSort('amount')} style={{ cursor: 'pointer', userSelect: 'none', textAlign: 'right' }}>
                  Amount{sortArrow('amount')}
                </th>
                <th>Category</th>
                <th className="hide-mobile">Kind</th>
                <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Status{sortArrow('status')}
                </th>
                {/* Actions column removed — row click opens the workflow item where every action lives.
                    "Link to account" is still needed for legacy "Other" expenses, so it hangs off the account cell instead. */}
              </tr>
            </thead>
            <tbody>
              {expenses.map(exp => {
                const checked = batchSelectedIds.includes(exp.id);
                const eligible = isBatchSelectableForRow(exp);
                return (
                <tr key={exp.id} style={{ cursor: 'pointer', background: checked ? '#fef3c715' : undefined }}
                  onClick={async () => {
                    // Reimbursement expenses → workflow item detail page (the
                    // single source of truth for approve / cut-check actions).
                    // Company expenses don't have a workflow item; show a
                    // read-only detail modal so the receipt/notes are viewable.
                    if (exp.is_reimbursement) {
                      try {
                        const wfItem = await api.lookupWorkflowItem('expense', exp.id);
                        if (wfItem?.id) {
                          navigate(`/workflow/items/${wfItem.id}`);
                          return;
                        }
                      } catch { /* fall through to view modal */ }
                    }
                    setViewExpense(exp);
                  }}>
                  {canManage && (
                    <td
                      onClick={e => e.stopPropagation()}
                      style={{ width: 36, textAlign: 'center', padding: '0 4px' }}
                    >
                      {isBatchableRow(exp) ? (
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!eligible && !checked}
                          onChange={() => toggleBatch(exp)}
                          title={
                            !eligible && !checked
                              ? `Already batching for ${batchAnchorName}. Uncheck to start fresh.`
                              : 'Add to batch payment'
                          }
                          style={{
                            width: 18, height: 18, cursor: (!eligible && !checked) ? 'not-allowed' : 'pointer',
                            opacity: (!eligible && !checked) ? 0.3 : 1,
                          }}
                        />
                      ) : null}
                    </td>
                  )}
                  <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: '#64748b' }}>{formatDate(exp.expense_date)}</td>
                  <td style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>{getEmployeeName(exp)}</td>
                  <td style={{ fontSize: 13, color: '#334155' }} className="hide-mobile">
                    {getAccountName(exp)}
                    {exp.account_name_other && (!exp.linked_accounts || exp.linked_accounts.length === 0) && (
                      <>
                        <span style={{ fontSize: 9, fontWeight: 700, color: '#d97706', background: '#fef3c7', padding: '1px 5px', borderRadius: 4, marginLeft: 6 }}>OTHER</span>
                        {canManage && (
                          <button
                            onClick={e => { e.stopPropagation(); setLinkExpense(exp); setLinkAccount(null); setLinkUseOther(false); }}
                            style={{
                              fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4, marginLeft: 6,
                              background: 'transparent', color: '#d97706', border: '1px solid #f59e0b60',
                              cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                            title="Link this expense to an account"
                          >🔗 Link</button>
                        )}
                      </>
                    )}
                    {exp.linked_accounts && exp.linked_accounts.length > 1 && (
                      <span style={{ fontSize: 9, fontWeight: 700, color: '#6d28d9', background: '#f5f3ff', padding: '1px 5px', borderRadius: 4, marginLeft: 6 }}>{exp.linked_accounts.length} ACCOUNTS</span>
                    )}
                  </td>
                  <td className="hide-mobile">
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#6d28d9', textTransform: 'capitalize' }}>
                      {TYPE_LABELS[exp.reimbursement_type] || exp.reimbursement_type}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
                    {formatMoney(exp.amount)}
                  </td>
                  <td>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                      background: `${CATEGORY_COLORS[exp.category || 'sales']}15`,
                      color: CATEGORY_COLORS[exp.category || 'sales'],
                    }} title={exp.category === 'other' && exp.category_description ? exp.category_description : ''}>
                      {CATEGORY_LABELS[exp.category || 'sales']}
                      {exp.category === 'other' && exp.category_description && (
                        <span style={{ marginLeft: 4, fontWeight: 500, opacity: 0.85 }}>
                          &mdash; {exp.category_description}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="hide-mobile">
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                      background: exp.is_reimbursement ? '#ede9fe' : '#f1f5f9',
                      color: exp.is_reimbursement ? '#6d28d9' : '#64748b',
                    }}>
                      {exp.is_reimbursement ? 'Reimburse' : 'Company'}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 6,
                      background: `${STATUS_COLORS[exp.status] || '#64748b'}15`,
                      color: STATUS_COLORS[exp.status] || '#64748b',
                      textTransform: 'uppercase',
                    }}>
                      {STATUS_LABELS[exp.status] || exp.status}
                      {exp.check_number && exp.status === 'paid' && ` #${exp.check_number}`}
                    </span>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Link-to-account modal */}
      {linkExpense && (
        <ModalRoot onClick={() => { setLinkExpense(null); setLinkAccount(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h2>Link to Account</h2>
              <button className="modal-close" onClick={() => setLinkExpense(null)}>&times;</button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: 16 }}>
              <div style={{ background: '#fef3c7', borderRadius: 10, padding: 14, fontSize: 13, border: '1px solid #fde68a' }}>
                <strong>"{linkExpense.account_name_other}"</strong> — {formatMoney(linkExpense.amount)} on {formatDate(linkExpense.expense_date)}
                {linkExpense.employee_name && <span style={{ color: '#92400e' }}> ({linkExpense.employee_name})</span>}
              </div>
              <div style={{ fontSize: 13, color: '#64748b' }}>
                Search for the account to attach this expense to. This is useful when you submitted an expense for a prospect that has since been signed.
              </div>
              <AccountSelector
                value={linkAccount}
                onChange={setLinkAccount}
                useOther={linkUseOther}
                onToggleOther={setLinkUseOther}
                otherValue=""
                onOtherChange={() => {}}
                label="Link to Account"
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setLinkExpense(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleLink} disabled={linking || !linkAccount}
                style={{ background: '#d97706' }}>
                {linking ? 'Linking...' : 'Link to Account'}
              </button>
            </div>
          </div>
        </ModalRoot>
      )}

      {/* Read-only detail view — company expenses are records, not approvals.
          Reimbursables click through to the workflow item page where actions
          live. This modal exists only so company expense rows show receipt +
          notes on click. */}
      {viewExpense && (
        <ModalRoot onClick={() => setViewExpense(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h2>Expense Details</h2>
              <button className="modal-close" onClick={() => setViewExpense(null)}>&times;</button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: 14 }}>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14, display: 'grid', gap: 8, fontSize: 13 }}>
                <div><strong>Submitted by:</strong> {getEmployeeName(viewExpense)}</div>
                <div><strong>Account:</strong> {getAccountName(viewExpense)}</div>
                <div><strong>Type:</strong> {TYPE_LABELS[viewExpense.reimbursement_type] || viewExpense.reimbursement_type}</div>
                <div><strong>Amount:</strong> <span style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{formatMoney(viewExpense.amount)}</span></div>
                <div><strong>Date:</strong> {formatDate(viewExpense.expense_date)}</div>
                <div><strong>Description:</strong> {viewExpense.description || '—'}</div>
                {viewExpense.people_present && <div><strong>Present:</strong> {viewExpense.people_present}</div>}
                <div>
                  <strong>Category:</strong> {CATEGORY_LABELS[viewExpense.category || 'sales']}
                  {viewExpense.category === 'other' && viewExpense.category_description && ` — ${viewExpense.category_description}`}
                </div>
                <div><strong>Kind:</strong> {viewExpense.is_reimbursement ? 'Reimbursement' : 'Company Expense'}</div>
                <div><strong>Status:</strong> {STATUS_LABELS[viewExpense.status] || viewExpense.status}</div>
                {viewExpense.receipt_url && (
                  <div>
                    <strong>Receipt:</strong>
                    <img src={viewExpense.receipt_url} alt="Receipt" style={{ display: 'block', maxWidth: '100%', maxHeight: 240, borderRadius: 8, marginTop: 6 }} />
                  </div>
                )}
              </div>
              {!viewExpense.is_reimbursement && (
                <div style={{ fontSize: 12, color: '#64748b', background: '#f1f5f9', borderRadius: 8, padding: '10px 14px' }}>
                  Company expenses are tracking records only — no approval or reimbursement. They'll aggregate on the property tracking page.
                </div>
              )}
              {viewExpense.status === 'voided' && (
                <div style={{ fontSize: 12, color: '#475569', background: '#f1f5f9', borderRadius: 8, padding: '10px 14px', border: '1px solid #cbd5e1' }}>
                  <strong>Voided.</strong> This expense is excluded from KPIs, reports, and totals.
                  {viewExpense.void_reason && <div style={{ marginTop: 4, fontStyle: 'italic' }}>Reason: {viewExpense.void_reason}</div>}
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap' }}>
              {/* Super-admin: void / unvoid. Voiding asks for an optional
                  reason via prompt. Unvoiding is a one-click confirm since
                  the prior status is restored from the audit stash. */}
              {isSuperAdmin && (
                viewExpense.status === 'voided' ? (
                  <button className="btn"
                    style={{ background: '#0f172a', color: '#fff', fontWeight: 600 }}
                    onClick={async () => {
                      if (!window.confirm('Restore this voided expense? It will start counting toward KPIs again.')) return;
                      try {
                        await api.unvoidExpense(viewExpense.id);
                        setViewExpense(null);
                        // Re-fetch list
                        await loadExpenses();
                      } catch (e) { alert(e.message || 'Unvoid failed'); }
                    }}>
                    Restore (Unvoid)
                  </button>
                ) : (
                  <button className="btn"
                    style={{ background: '#fee2e2', color: '#991b1b', fontWeight: 600, border: '1px solid #fca5a5' }}
                    onClick={async () => {
                      const reason = window.prompt(
                        'Void this expense?\n\n' +
                        'This removes it from KPIs, reports, and totals. ' +
                        'Reversible — you can restore it later from this same modal.\n\n' +
                        'Optional reason:',
                        ''
                      );
                      if (reason === null) return; // user cancelled
                      try {
                        await api.voidExpense(viewExpense.id, reason || null);
                        setViewExpense(null);
                        await loadExpenses();
                      } catch (e) { alert(e.message || 'Void failed'); }
                    }}>
                    Void Expense
                  </button>
                )
              )}
              <button className="btn btn-secondary" onClick={() => setViewExpense(null)} style={{ marginLeft: 'auto' }}>Close</button>
            </div>
          </div>
        </ModalRoot>
      )}

      {/* ─── Sticky batch payment bar ─────────────────────────────────────
          Bar starts at the sidebar's right edge (var(--sidebar-width) on
          desktop, 0 on mobile where sidebar is hidden) so it doesn't get
          covered by the sidebar's user-profile/logout section.
          Generous vertical padding + iOS safe-area inset prevents clipping
          at the viewport bottom. 110px page-bottom spacer reserves room so
          the last table row stays readable. */}
      {canManage && batchSelectedIds.length > 0 && (
        <>
          <div aria-hidden="true" style={{ height: 110 }} />
          <div className="batch-pay-bar m-float-bar" style={{
            position: 'fixed', bottom: 0, right: 0, zIndex: 50,
            background: '#0f172a', color: '#fff',
            padding: '20px 24px calc(20px + env(safe-area-inset-bottom)) 24px',
            boxShadow: '0 -4px 16px rgba(0,0,0,0.25)',
            display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.3 }}>
                Batch payment for <strong style={{ color: '#fbbf24' }}>{batchAnchorName}</strong>
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4, lineHeight: 1.3 }}>
                {batchSelectedIds.length} expense{batchSelectedIds.length === 1 ? '' : 's'} selected · {formatMoney(batchTotal)}
              </div>
            </div>
            <button
              className="btn btn-secondary"
              onClick={clearBatch}
              style={{ background: 'transparent', color: '#fff', border: '1px solid #ffffff40' }}
            >Cancel</button>
            <button
              className="btn btn-primary"
              onClick={() => setBatchModalOpen(true)}
              style={{ background: '#10b981', border: 0, fontWeight: 700 }}
            >Process Together</button>
          </div>
          {/* Sidebar-aware left offset — desktop respects --sidebar-width,
              collapsed sidebar uses --sidebar-collapsed, mobile gets 0. */}
          <style>{`
            .batch-pay-bar { left: var(--sidebar-width, 260px); }
            .sidebar.collapsed ~ * .batch-pay-bar,
            body:has(.sidebar.collapsed) .batch-pay-bar { left: var(--sidebar-collapsed, 72px); }
            @media (max-width: 768px) { .batch-pay-bar { left: 0 !important; } }
          `}</style>
        </>
      )}

      {/* ─── Batch payment modal ───────────────────────────────────────── */}
      {batchModalOpen && (
        <ModalRoot onClick={() => !batchSubmitting && setBatchModalOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h2>Process Batch Payment</h2>
              <button className="modal-close" onClick={() => !batchSubmitting && setBatchModalOpen(false)}>&times;</button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: 16 }}>
              <div style={{ background: '#f1f5f9', borderRadius: 10, padding: 14, fontSize: 13 }}>
                <div><strong>Recipient:</strong> {batchAnchorName}</div>
                <div><strong>Expenses:</strong> {batchSelectedIds.length}</div>
                <div><strong>Total:</strong> {formatMoney(batchTotal)}</div>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: 200, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                {batchSelectedIds.map(id => {
                  const e = expenses.find(x => x.id === id);
                  if (!e) return null;
                  return (
                    <li key={id} style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
                      <span>{formatDate(e.expense_date)} · {TYPE_LABELS[e.reimbursement_type] || e.reimbursement_type}</span>
                      <strong>{formatMoney(e.amount)}</strong>
                    </li>
                  );
                })}
              </ul>
              <label style={{ display: 'grid', gap: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>Check number (optional)</span>
                <input
                  type="text"
                  value={batchCheckNumber}
                  onChange={e => setBatchCheckNumber(e.target.value)}
                  placeholder="e.g., 1234"
                  style={{ height: 40, padding: '0 12px', fontSize: 16, border: '1px solid #cbd5e1', borderRadius: 8 }}
                  autoFocus
                />
                <span style={{ fontSize: 12, color: '#64748b' }}>Same check number applied to all {batchSelectedIds.length} expenses.</span>
              </label>
              {batchError && (
                <div style={{ background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 8, fontSize: 13 }}>
                  {batchError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setBatchModalOpen(false)} disabled={batchSubmitting}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={submitBatch}
                disabled={batchSubmitting}
                style={{ background: '#10b981', border: 0 }}
              >
                {batchSubmitting ? 'Processing…' : `Mark ${batchSelectedIds.length} Paid`}
              </button>
            </div>
          </div>
        </ModalRoot>
      )}
    </div>
  );
}
